const FALLBACK_PRINTER_HINT = process.env.REACT_APP_BLUETOOTH_PRINTER_HINT || 'PT-380';
const LABEL_FALLBACK_HINT = process.env.REACT_APP_LABEL_PRINTER_HINT || FALLBACK_PRINTER_HINT;
const PREF_KEY = 'preferredPrinterHints';
const LEGACY_KEY = 'preferredPrinterHint';
const DEFAULT_PORT = 9100;
const CLEAR_SENTINEL = '__cleared__';

const normalizeJobType = (jobType) => {
  if (!jobType || typeof jobType !== 'string') {
    return 'receipt';
  }
  return jobType.toLowerCase();
};

const readHintMap = () => {
  if (typeof window === 'undefined') {
    return {};
  }
  try {
    const raw = window.localStorage?.getItem(PREF_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        return parsed;
      }
    }
  } catch (err) {
    console.warn('Failed to parse preferredPrinterHints', err);
  }
  return {};
};

const writeHintMap = (map) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage?.setItem(PREF_KEY, JSON.stringify(map));
  } catch (err) {
    console.warn('Failed to persist preferredPrinterHints', err);
  }
};

const defaultConfigForType = (type) => ({
  transport: 'bluetooth',
  bluetoothName: type === 'label' ? LABEL_FALLBACK_HINT : FALLBACK_PRINTER_HINT,
  host: '',
  port: DEFAULT_PORT,
});

const coerceNumber = (value, fallback = DEFAULT_PORT) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value.trim());
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return fallback;
};

export const getPreferredPrinterConfig = (jobType = 'receipt') => {
  const normalizedType = normalizeJobType(jobType);
  const hintMap = readHintMap();
  const stored = hintMap[normalizedType];
  if (stored && typeof stored === 'object') {
    const transport = stored.transport === 'network' ? 'network' : 'bluetooth';
    let bluetoothName = (stored.bluetoothName || stored.hint || '').trim();
    const wasCleared = bluetoothName === CLEAR_SENTINEL;
    if (wasCleared) {
      bluetoothName = '';
    }
    const host = (stored.host || stored.networkHost || '').trim();
    const port = coerceNumber(stored.port ?? stored.networkPort, DEFAULT_PORT);
    return {
      transport,
      bluetoothName:
        transport === 'bluetooth'
          ? wasCleared
            ? ''
            : bluetoothName || defaultConfigForType(normalizedType).bluetoothName
          : bluetoothName,
      host,
      port: port > 0 && port <= 65535 ? port : DEFAULT_PORT,
    };
  }
  if (stored && typeof stored === 'string') {
    return {
      transport: 'bluetooth',
      bluetoothName: stored.trim(),
      host: '',
      port: DEFAULT_PORT,
    };
  }

  if (typeof window !== 'undefined') {
    try {
      const legacy = window.localStorage?.getItem(LEGACY_KEY);
      if (legacy && legacy.trim()) {
        return {
          transport: 'bluetooth',
          bluetoothName: legacy.trim(),
          host: '',
          port: DEFAULT_PORT,
        };
      }
    } catch (err) {
      console.warn('Failed to read legacy preferredPrinterHint', err);
    }
  }

  return defaultConfigForType(normalizedType);
};

export const hasPreferredPrinterConfig = (jobType = 'receipt') => {
  if (typeof window === 'undefined') {
    return false;
  }
  const normalizedType = normalizeJobType(jobType);
  const hintMap = readHintMap();
  const stored = hintMap[normalizedType];
  if (stored && typeof stored === 'object') {
    const transport = stored.transport === 'network' ? 'network' : 'bluetooth';
    const bluetoothName = (stored.bluetoothName || stored.hint || '').trim();
    const host = (stored.host || stored.networkHost || '').trim();
    if (transport === 'network') {
      return Boolean(host);
    }
    return Boolean(bluetoothName) && bluetoothName !== CLEAR_SENTINEL;
  }
  if (stored && typeof stored === 'string') {
    return Boolean(stored.trim());
  }

  try {
    const legacy = window.localStorage?.getItem(LEGACY_KEY);
    return Boolean(legacy && legacy.trim());
  } catch (err) {
    console.warn('Failed to read legacy preferredPrinterHint', err);
  }

  return false;
};

export const setPreferredPrinterConfig = (jobType = 'receipt', config) => {
  if (typeof window === 'undefined') return;
  const normalizedType = normalizeJobType(jobType);
  const hintMap = readHintMap();
  if (!config) {
    delete hintMap[normalizedType];
    writeHintMap(hintMap);
    return;
  }

  let bluetoothName = (config.bluetoothName || '').trim();
  if (bluetoothName === '') {
    bluetoothName = CLEAR_SENTINEL; // explicit cleared state
  }

  const normalized = {
    transport: config.transport === 'network' ? 'network' : 'bluetooth',
    bluetoothName,
    host: (config.host || '').trim(),
    port: coerceNumber(config.port, DEFAULT_PORT),
  };

  if (normalized.transport === 'bluetooth' && normalized.bluetoothName !== CLEAR_SENTINEL && !normalized.bluetoothName) {
    normalized.bluetoothName = defaultConfigForType(normalizedType).bluetoothName;
  }

  hintMap[normalizedType] = normalized;
  writeHintMap(hintMap);
};

export const getPreferredPrinterHint = (jobType = 'receipt') => {
  const config = getPreferredPrinterConfig(jobType);
  return config.bluetoothName || '';
};

export const setPreferredPrinterHint = (jobType = 'receipt', value) => {
  setPreferredPrinterConfig(jobType, {
    transport: 'bluetooth',
    bluetoothName: value || '',
    host: '',
    port: DEFAULT_PORT,
  });
};
